// TrackEAT — Documento di validità dei registri HACCP digitali (T-178).
//
// FONTE UNICA (single source of truth). Questo file è reso in DUE posti:
//   1. In-app: modale offline in Impostazioni → Conformità (apps/mobile).
//   2. Web: pagina pubblica trackeat.eu/validita-registri, GENERATA da questo
//      contenuto via `landing/scripts/gen-validita-registri.mjs` (+ "Scarica PDF").
// NON duplicare il testo altrove: aggiornare SOLO qui, poi rigenerare la landing
// e ripubblicare l'OTA. Le classi `cd-*` sono stilizzate sia dalla modale sia
// dalla pagina landing.
//
// ⚠️ Compliance: contenuto rivisto/firmato dal tecnologo alimentare. Mantenere
// l'onestà tecnica (es. timestamp RFC3161 = elettronico NON qualificato eIDAS).

/** Versione/data del documento. Bump ad ogni modifica sostanziale del testo. */
export const COMPLIANCE_DOC_VERSION = '1.0 — 4 giugno 2026';

export const COMPLIANCE_DOC_TITLE =
  'Validità giuridica dei registri HACCP digitali compilati con TrackEAT';

/**
 * Corpo del documento in HTML semantico (senza chrome di pagina).
 * Reso via dangerouslySetInnerHTML nella modale in-app e iniettato nel template
 * della landing dal generatore.
 */
export const COMPLIANCE_DOC_BODY_HTML = `
<p class="cd-updated">Versione ${COMPLIANCE_DOC_VERSION}</p>

<div class="cd-callout">
  <strong>In breve (per il ristoratore).</strong> La normativa <em>non</em> ti
  obbliga a tenere i registri HACCP su carta. Quello che la legge richiede è la
  <strong>sostanza</strong>: registrazioni veritiere, leggibili, integre,
  disponibili al controllo e conservate per un periodo adeguato. TrackEAT è
  costruito proprio per garantire queste qualità in forma digitale — e questo
  documento spiega su quali norme ci basiamo e con quali strumenti tecnici lo
  assicuriamo.
</div>

<h2>1. Scopo del documento</h2>
<p>
  Il presente documento illustra il quadro normativo in base al quale i registri
  dell'autocontrollo HACCP possono essere compilati, gestiti e conservati in
  formato digitale tramite l'applicazione <strong>TrackEAT</strong>, e descrive
  le misure tecniche con cui TrackEAT garantisce che i registri esportati siano
  <strong>integri, autentici, disponibili e difendibili</strong> in sede di
  controllo ufficiale. È destinato sia all'operatore del settore alimentare
  (OSA) sia all'autorità competente (ASL/NAS) in caso di verifica.
</p>

<h2>2. Quadro normativo di riferimento</h2>
<ul>
  <li>
    <strong>Reg. (CE) n. 852/2004</strong> sull'igiene dei prodotti alimentari —
    <em>art. 5</em>: obbligo per l'OSA di predisporre, attuare e mantenere
    procedure permanenti basate sui principi del sistema HACCP, e di
    <strong>predisporre e conservare documenti e registrazioni</strong> adeguati
    a dimostrarne l'effettiva applicazione. Lo stesso articolo introduce il
    <strong>principio di flessibilità e proporzionalità</strong>: documenti e
    registrazioni devono essere <em>commisurati alla natura e alle dimensioni
    dell'impresa</em>, senza imporre una forma predeterminata.
  </li>
  <li>
    <strong>Reg. (CE) n. 178/2002</strong> (legislazione alimentare generale) —
    <em>art. 18</em>: obbligo di <strong>rintracciabilità</strong> in tutte le
    fasi della produzione, trasformazione e distribuzione.
  </li>
  <li>
    <strong>D.Lgs. 6 novembre 2007, n. 193</strong> — attuazione in Italia e
    disciplina sanzionatoria dei Reg. (CE) 852/2004 e 853/2004.
  </li>
  <li>
    <strong>Reg. (UE) 2017/625</strong> sui controlli ufficiali — definisce le
    modalità con cui l'autorità verifica la documentazione dell'autocontrollo.
  </li>
</ul>

<h2>3. Assenza dell'obbligo di forma cartacea</h2>
<p>
  Nessuna delle norme citate impone che i registri dell'autocontrollo siano
  tenuti su <strong>supporto cartaceo</strong>. Il Reg. (CE) 852/2004 richiede
  che documenti e registrazioni siano <em>predisposti, mantenuti aggiornati e
  conservati</em> e resi <em>disponibili</em> all'autorità: requisiti di
  contenuto e di disponibilità, non di formato. Di conseguenza la tenuta
  digitale è pienamente ammessa, a condizione che siano garantite le stesse
  qualità sostanziali del cartaceo — anzi, in forma più robusta:
  <strong>autenticità, integrità, leggibilità nel tempo, disponibilità e
  conservazione</strong>.
</p>

<h2>4. Validità giuridica del documento e della registrazione informatica</h2>
<ul>
  <li>
    <strong>Reg. (UE) n. 910/2014 (eIDAS) — art. 46</strong>: a un documento
    elettronico <strong>non possono essere negati gli effetti giuridici né
    l'ammissibilità come prova</strong> in giudizio per il solo fatto della sua
    forma elettronica (principio di non discriminazione).
  </li>
  <li>
    <strong>eIDAS — art. 41</strong>: anche alla <strong>validazione temporale
    elettronica</strong> non possono essere negati effetti giuridici e
    ammissibilità come prova per il solo fatto della forma elettronica; la
    validazione temporale <em>qualificata</em> gode inoltre della
    <strong>presunzione di esattezza di data e ora e di integrità dei dati</strong>
    a cui è associata.
  </li>
  <li>
    <strong>Codice dell'Amministrazione Digitale (D.Lgs. 82/2005) — art. 20</strong>
    e norme sulla <strong>conservazione</strong> (artt. 43-44): riconoscono valore
    al documento informatico in funzione delle sue caratteristiche di qualità,
    sicurezza, integrità e immodificabilità.
  </li>
</ul>
<p>
  In sintesi: un registro HACCP digitale è una prova ammissibile a tutti gli
  effetti; la sua <em>forza</em> probatoria dipende dalle garanzie tecniche di
  integrità e di datazione che lo accompagnano — esattamente ciò che TrackEAT
  fornisce (punto 5).
</p>

<h2>5. Come TrackEAT garantisce integrità, autenticità e disponibilità</h2>
<ul>
  <li>
    <strong>Registro immutabile (append-only).</strong> Ogni fatto rilevante
    (temperature, sanificazioni, etichette, carichi/scarichi, documenti) è
    salvato come <em>evento</em> non modificabile né cancellabile. Una correzione
    non sovrascrive il passato: genera un <strong>evento compensativo</strong>,
    così la storia resta integra e ricostruibile.
  </li>
  <li>
    <strong>Catena di hash crittografici (SHA-256).</strong> Ogni evento è
    concatenato al precedente tramite impronta crittografica: qualunque
    alterazione, anche di un solo carattere, <strong>rompe la catena</strong> e
    diventa rilevabile. È la prova tecnica che i dati non sono stati manomessi.
  </li>
  <li>
    <strong>Ancoraggio temporale settimanale.</strong> Con cadenza periodica
    TrackEAT calcola una radice (Merkle root) dell'intero registro e la
    sottopone a una <strong>marca temporale RFC 3161</strong>: ne risulta una
    prova che, a quella data, i dati esistevano in quella forma e non sono stati
    successivamente alterati.
  </li>
  <li>
    <strong>Rintracciabilità (Reg. 178/2002).</strong> Lotti, DDT/fatture e
    catena di trasformazione sono collegati; ogni etichetta riporta un QR che
    apre la pagina pubblica di tracciabilità del prodotto.
  </li>
  <li>
    <strong>Separazione e controllo accessi.</strong> I dati di ogni attività
    sono isolati a livello di database (Row Level Security) e ogni accesso/azione
    sensibile è tracciato in un registro di audit.
  </li>
  <li>
    <strong>Disponibilità ed esportazione su richiesta.</strong> I registri
    sono esportabili in qualsiasi momento (PDF/CSV, funzione "Report ASL") e gli
    originali (es. scansioni dei DDT) restano consultabili: il controllo trova
    tutto, subito.
  </li>
</ul>

<h2>6. Nota di trasparenza sul timestamp</h2>
<div class="cd-note">
  La marca temporale impiegata da TrackEAT è di tipo <strong>RFC 3161</strong>
  ed è, ai sensi dell'art. 41 eIDAS, una <strong>validazione temporale
  elettronica</strong> — pienamente <em>ammissibile come prova</em>. Non è, allo
  stato attuale, una validazione temporale <strong>qualificata</strong> (che
  richiederebbe un prestatore di servizi fiduciari qualificato) e non beneficia
  quindi della presunzione legale piena dell'art. 41, par. 2. L'architettura di
  TrackEAT è però progettata per essere compatibile con un'eventuale
  evoluzione verso marca temporale qualificata e conservazione a norma, ove
  richiesto. Dichiariamo questo limite per trasparenza e per garantire la
  difendibilità del documento.
</div>

<h2>7. I nostri impegni</h2>
<p>Con TrackEAT ci impegniamo, per i registri gestiti tramite l'applicazione, a:</p>
<ul>
  <li>preservarne l'<strong>integrità</strong> (immutabilità + catena hash + ancoraggio temporale) e renderne <strong>rilevabile</strong> qualunque manomissione;</li>
  <li>garantirne la <strong>disponibilità</strong> e l'<strong>esportazione fedele</strong> ai dati effettivamente registrati, in formati leggibili (PDF/CSV);</li>
  <li>non consentire <strong>alterazioni silenziose</strong>: ogni rettifica è tracciata e ricostruibile;</li>
  <li>assicurarne la <strong>leggibilità nel tempo</strong> e la <strong>conservazione</strong> per il periodo dichiarato al punto 8;</li>
  <li>mantenere <strong>trasparenza</strong> sui limiti tecnici (punto 6) e adeguare gli strumenti all'evoluzione normativa.</li>
</ul>

<h2>8. Conservazione e disponibilità per i controlli</h2>
<p>
  I registri HACCP sono conservati in forma digitale e resi disponibili al
  controllo per un periodo <strong>non inferiore a 12 mesi</strong>, fermo
  restando che l'OSA può estenderlo in funzione della propria valutazione del
  rischio e della durabilità dei prodotti. I documenti con rilevanza fiscale
  (DDT, fatture) sono conservati per <strong>10 anni</strong>, come da normativa
  civilistica e tributaria. In sede di verifica, i registri sono esportabili
  immediatamente dall'app.
</p>

<h2>9. Limiti e responsabilità</h2>
<p>
  Questo documento attesta la <strong>conformità metodologica</strong> dello
  strumento e della sua impostazione ai regolamenti citati; <strong>non
  sostituisce</strong> il manuale di autocontrollo e il piano HACCP specifici
  dell'attività, né solleva l'OSA dalla responsabilità della
  <strong>veridicità</strong> di quanto registra. TrackEAT garantisce
  l'integrità, l'autenticità tecnica e la disponibilità di <em>ciò che viene
  registrato</em>; la correttezza dei controlli e dei dati inseriti resta in
  capo all'operatore.
</p>

<h2>10. Attestazione</h2>
<div class="cd-sign">
  <p>
    <strong>Garanzie tecniche fornite da TrackEAT</strong><br>
    TrackEAT — Pierpaolo Criscenzo —
    P.IVA 03171840840 — <a href="mailto:support@trackeat.eu">support@trackeat.eu</a>.
  </p>
  <p>
    <strong>Attestazione professionale</strong><br>
    Il sottoscritto <strong>Pierpaolo Criscenzo</strong>, <strong>Tecnologo
    Alimentare</strong> iscritto all'<strong>Ordine dei Tecnologi Alimentari di
    Sicilia e Sardegna (OTASS)</strong> al n. <strong>469</strong>, attesta che
    l'impostazione metodologica dei registri HACCP digitali gestiti con TrackEAT
    è conforme ai regolamenti richiamati e idonea a garantire l'integrità,
    l'autenticità e la disponibilità delle registrazioni.
  </p>
  <p class="cd-sign-line">
    Luogo e data: ____________________  &nbsp;&nbsp; Firma: ____________________
  </p>
</div>
`;
